/**
 * "Mürekkep ve limon" tasarım değerleri — tuvaldeki Bileşen kütüphanesi panosunun
 * kod karşılığı. Mürekkep eylemi taşır, limon yalnızca seçili / yeni / şu an
 * demek içindir; durum renkleri yalnızca durum, proje renkleri yalnızca kimlik.
 */
export const colors = {
  ink: '#0F1115',
  night: '#0E1015',
  lime: '#C8F169',
  limeSoft: '#E6F9B8',
  graphite: '#3A3F4B',
  ash: '#5F6675',
  mute: '#8A90A0',
  line: '#E6E8EC',
  lineStrong: '#D3D7DE',
  divider: '#F0F1F3',
  canvas: '#F4F5F7',
  surface: '#FFFFFF',
  surfaceMuted: '#F7F8FA',
  white: '#FFFFFF',
} as const

export const status = {
  success: { bg: '#E6F6EC', fg: '#1A7F45' },
  danger: { bg: '#FDECEA', fg: '#B42318' },
  warning: { bg: '#FEF3E2', fg: '#8A4A06' },
  info: { bg: '#EAF0FE', fg: '#2849B8' },
  neutral: { bg: '#F1F2F4', fg: '#3A3F4B' },
} as const

export type ProjectRoleKey = 'OWNER' | 'EDITOR' | 'VIEWER' | 'TESTER'

export const roles: Record<ProjectRoleKey, { label: string; bg: string; fg: string }> = {
  OWNER: { label: 'Sahip', bg: '#0F1115', fg: '#FFFFFF' },
  EDITOR: { label: 'Düzenleyebilir', bg: '#E7F5F2', fg: '#0B6B5F' },
  VIEWER: { label: 'Görüntüleyebilir', bg: '#EEF0F3', fg: '#4A5060' },
  TESTER: { label: 'Test eden', bg: '#F0EBFF', fg: '#5B3CC4' },
}

export const visibility = {
  PRIVATE: { label: 'Yalnızca ben', bg: '#F1F2F4', fg: '#3A3F4B' },
  SHARED: { label: 'Paylaşılan', bg: '#EAF0FE', fg: '#2849B8' },
} as const

/** Proje kimlik renkleri; proje kimliğinden kararlı biçimde seçilir. */
export const projectPalette = ['#2F5BEA', '#0F8A7E', '#7048E8', '#D9480F', '#0C8599', '#C2255C', '#4250C4', '#5C7F0D'] as const

export function projectColor(id: string): string {
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0
  return projectPalette[Math.abs(h) % projectPalette.length]
}

/** İki harflik monogram ("Şubat Raporları" → "ŞR"). */
export function monogram(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const letters = parts.length >= 2 ? parts[0][0] + parts[1][0] : (parts[0] ?? '?').slice(0, 2)
  return letters.toLocaleUpperCase('tr')
}

export type FileKindKey = 'WORD' | 'EXCEL' | 'PRESENTATION' | 'PDF' | 'IMAGE' | 'TEXT' | 'OTHER'

export const fileKinds: Record<FileKindKey, { short: string; label: string; color: string }> = {
  WORD: { short: 'W', label: 'Word', color: '#2B5FD9' },
  EXCEL: { short: 'X', label: 'Excel', color: '#1F7A45' },
  PRESENTATION: { short: 'P', label: 'Sunum', color: '#C8501C' },
  PDF: { short: 'PDF', label: 'PDF', color: '#C4271D' },
  IMAGE: { short: 'IMG', label: 'Görsel', color: '#8E3BB8' },
  TEXT: { short: 'TXT', label: 'Metin', color: '#5F6675' },
  OTHER: { short: '•••', label: 'Diğer', color: '#5F6675' },
}

/** Uzantıdan dosya türü. Sunucudaki FileKind ile aynı eşleme (sunucunun kind'ı yoksa kullanılır). */
const KIND_EXT: Array<[FileKindKey, string[]]> = [
  ['WORD', ['doc', 'docx', 'odt', 'rtf', 'dot', 'dotx']],
  ['EXCEL', ['xls', 'xlsx', 'ods', 'csv', 'xlt', 'xltx']],
  ['PRESENTATION', ['ppt', 'pptx', 'odp', 'pot', 'potx']],
  ['PDF', ['pdf']],
  ['IMAGE', ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg']],
  ['TEXT', ['txt', 'md', 'json', 'xml', 'yml', 'yaml', 'log']],
]
export function fileKindOf(extensionOrName: string): FileKindKey {
  const ext = extensionOrName.toLowerCase().split('.').pop() ?? ''
  return KIND_EXT.find(([, list]) => list.includes(ext))?.[0] ?? 'OTHER'
}

export const radius = { xs: 6, sm: 9, md: 11, lg: 14, xl: 18, xxl: 24, pill: 999 } as const

export const space = { 1: 4, 2: 8, 3: 12, 4: 16, 5: 20, 6: 24, 8: 32 } as const

export const font = {
  regular: 'Geist_400Regular',
  medium: 'Geist_500Medium',
  semibold: 'Geist_600SemiBold',
  bold: 'Geist_700Bold',
  mono: 'GeistMono_400Regular',
  monoMedium: 'GeistMono_500Medium',
} as const

export const type = {
  display: { size: 34, lineHeight: 36, letterSpacing: -1.5 },
  title: { size: 24, lineHeight: 28, letterSpacing: -0.8 },
  section: { size: 19, lineHeight: 24, letterSpacing: -0.4 },
  body: { size: 15, lineHeight: 22, letterSpacing: 0 },
  caption: { size: 13, lineHeight: 18, letterSpacing: 0 },
  label: { size: 11, lineHeight: 14, letterSpacing: 1.1 },
} as const
